import { useEffect, useState } from 'react';
import { findByValue } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { currentDate, getDueInDate } from '@witmetrics/utils/time';
import type {
  DecoratedTaskTemplate,
  DecoratedUnisonProject,
} from '@witmetrics/utils';
import {
  useActivePractice,
  useCurrentUser,
  useDispatch,
  usePracticeTaskTemplates,
  useProject,
  useProjectTaskFromTaskId,
} from '@witmetrics/data-store';
import { buildUserOption } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import { useDate } from '@/hooks/useDate';
import { useString } from '@/hooks/useString';
import { useToggle } from '@/hooks/useToggle';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import { showSuccessToaster } from '@/utils/toasters';
import Button from '@/components/Button';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import DateTimeInput, {
  DEADLINE_PRESETS,
} from '@/components/inputs/DateTimeInput';
import SearchableCaseDropdown from '@/components/inputs/SearchableCaseDropdown';
import SearchableDropdown, {
  type SearchableDropdownOption,
} from '@/components/inputs/SearchableDropdown';
import TextField from '@/components/inputs/TextField';
import TaskTemplateSearchDialog from '@/dialogs/TaskTemplateSearchDialog';
import { StickyNoteIcon } from '@/icons';
import {
  buildTaskData,
  classes,
  createNewTask,
  fetchMatchingProjects,
  fetchPracticeUsers,
  fetchTaskTemplates,
  getInitialDate,
  searchAssignedTo,
  updateTask,
  validateFields,
} from './utils';

export type UnisonProjectTaskDialogProps = {
  taskId?: number;
  projectId?: number | null;
  onClose: () => void;
};

export default function UnisonProjectTaskDialog({
  taskId,
  projectId,
  onClose,
}: UnisonProjectTaskDialogProps) {
  const dispatch = useDispatch();
  const { onApiError, activeCalendarDate } = useAppState();
  const currentUser = useCurrentUser();
  const activePractice = useActivePractice();
  const projectTask = useProjectTaskFromTaskId(taskId);
  const initialProject = useProject(projectId);
  const taskTemplates = usePracticeTaskTemplates(activePractice?.id);
  const [practiceUsers, setPracticeUsers] = useState([
    buildUserOption(currentUser!),
  ]);
  const [project, setProject] = useState<DecoratedUnisonProject | null>(
    initialProject ?? null
  );
  const [assignedTo, setAssignedTo] =
    useState<SearchableDropdownOption<number> | null>(
      buildUserOption(currentUser!)
    );
  const [dueAt, setDueAt] = useDate(
    getInitialDate(activeCalendarDate, projectTask?.task)
  );
  const [description, setDescription] = useString();
  const { setInvalidFields } = useInvalidFields();
  const [isTemplateSearchOpen, toggleTemplateSearch] = useToggle();
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (activePractice) {
      handleFetchPracticeUsers();
      handleFetchTaskTemplates();
    }
  }, [activePractice]);

  useEffect(() => {
    if (projectTask) fillFields();
  }, [projectTask]);

  const fillFields = () => {
    const { task, unisonProject } = projectTask!;
    dispatch.addProjects([unisonProject!]);
    setDueAt(task!.dueAt);
    setProject(unisonProject!);
    setAssignedTo(buildUserOption(task!.assignedTo));
    setPracticeUsers([buildUserOption(task!.assignedTo)]);
    setDescription(task!.description);
  };

  const handleFetchTaskTemplates = async () => {
    try {
      const templates = await fetchTaskTemplates(activePractice!.id);
      dispatch.addTaskTemplates(templates);
    } catch (err) {
      onApiError(err, 'Error fetching task templates', () =>
        handleFetchTaskTemplates()
      );
    }
  };

  const handleSelectTemplate = (template: DecoratedTaskTemplate) => {
    setDescription(template.description);
    if (template.assignedTo) {
      const match = findByValue(practiceUsers, template.assignedTo.id);
      if (match) setAssignedTo(match);
    }
    if (template.dueIn) {
      setDueAt(getDueInDate(currentDate(), template.dueIn));
    }
  };

  const handleSave = async () => {
    try {
      toggleSaving(true);
      const invalid = validateFields({
        dueAt,
        project,
        assignedTo,
        description,
      });
      if (!isEmpty(invalid)) return setInvalidFields(invalid);
      const data = buildTaskData({
        dueAt: dueAt!,
        assignedTo: assignedTo!,
        description,
      });
      if (taskId) {
        dispatch.addTasks([await updateTask(taskId, data)]);
        showSuccessToaster('Task updated');
      } else {
        const result = await createNewTask(project!.id, data);
        dispatch.addTasks([result.task]);
        dispatch.addProjectTasks([result.unisonProjectTask]);
        showSuccessToaster('Task created');
      }
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving task', () => handleSave());
    }
  };

  const isSaveDisabled = () => {
    return (
      isSaving ||
      !isEmpty(validateFields({ dueAt, project, assignedTo, description }))
    );
  };

  const handleFetchPracticeUsers = async () => {
    try {
      const practiceId = activePractice!.id;
      const users = await fetchPracticeUsers(practiceId);
      setPracticeUsers(users);
    } catch (err) {
      onApiError(err, 'Error fetching practice users', () => {
        handleFetchPracticeUsers();
      });
    }
  };

  const handleSearchAssignedTo = async (searchQuery: string) => {
    return searchAssignedTo(searchQuery, practiceUsers);
  };

  const canUseTemplate = !taskId && !isEmpty(taskTemplates);

  return (
    <>
      {isTemplateSearchOpen && (
        <TaskTemplateSearchDialog
          onSelect={handleSelectTemplate}
          onClose={() => toggleTemplateSearch(false)}
        />
      )}
      <Dialog className={classes.dialog} onClose={onClose}>
        <DialogTitle
          title={taskId ? 'Edit Task' : 'New Task'}
          actions={
            canUseTemplate && (
              <Button
                size="sm"
                icon={<StickyNoteIcon />}
                onClick={() => toggleTemplateSearch(true)}>
                Templates
              </Button>
            )
          }
          onClose={onClose}
        />
        <DialogBody className={classes.body}>
          {!projectId && (
            <SearchableCaseDropdown
              disabled={!!taskId}
              className={classes.input}
              label="Select a Case"
              value={project}
              onSearch={fetchMatchingProjects}
              onSelect={setProject}
            />
          )}
          <TextField
            multiline
            className={classes.input}
            minRows={3}
            maxRows={5}
            label="Description"
            value={description}
            onChange={setDescription}
          />
          <div className={classes.row}>
            <div className={classes.rowItem}>
              <SearchableDropdown
                className={classes.input}
                label="Assigned To"
                value={assignedTo}
                initialOptions={practiceUsers}
                onSearch={handleSearchAssignedTo}
                onSelect={setAssignedTo}
              />
            </div>
            <div className={classes.rowItem}>
              <DateTimeInput
                disablePast={false}
                className={classes.input}
                label="Deadline"
                value={dueAt}
                presets={DEADLINE_PRESETS}
                onApply={() => null}
                onChange={setDueAt}
              />
            </div>
          </div>
        </DialogBody>
        <DefaultDialogActions
          disabled={isSaveDisabled()}
          pending={isSaving}
          confirmLabel="Save"
          pendingLabel="Saving"
          onCancel={onClose}
          onConfirm={handleSave}
        />
      </Dialog>
    </>
  );
}
